import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { FormikProvider, useFormik, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  Stack,
  Grid,
  TextareaAutosize,
  Typography,
  Divider,
  Avatar,
  Autocomplete,
  Alert,
  Snackbar,
} from "@common-ui/ui";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { createCustomer } from "../../services";
import AlertDialog from "./AlertDialog";
import { createFilterOptions } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { countries } from "../../services";
import { postNewCustomerAddress, updateOrgCustomerDetails } from "../../services/orgCustomer";
import { getClientImagePutUrl, putClientImage } from "../../services";
import DeleteIcon from '@mui/icons-material/Delete'
import ImageIcon from '@mui/icons-material/Image';
import styled from "@emotion/styled";
import { validateImageType, getAvatarInitials } from "../../utils";
const BUTTONS = {
  ADD_BUTTON: { "label": "", "variant": "text", "icon": <ImageIcon />, "size": "small" },
  EDIT_BUTTON: { "label": "", "variant": "text", "icon": <DeleteIcon />, "size": "small" }
};

const org = window?.localStorage?.getItem("REDUX_CURRENT_ORGANIZATION");
const orgID = (org) ? JSON.parse(org) : undefined;

export function CreateCustomerDialog(props) {
  // eslint-disable-next-line no-unused-vars
  let router = useHistory();
  const currentOrgId = useSelector(
    (state) => state.organization?.currentOrganization?.id
  );

  const [open, setOpen] = useState(false);
  const [customerSystemId, setCustomerSystemId] = useState(null);
  const [clientAvatarInitials, setClientAvatarInitials] = useState("");
  const [toast, setToast] = useState([]);
  const [clientImageBtnProps, setClientImageBtnProps] = useState(BUTTONS.ADD_BUTTON);
  const [clientImageUrl, setClientImageUrl] = useState(null);
  const [clientImageFileType, setClientImageFileType] = useState("");
  const [clientImage, setClientImage] = useState("");

  useEffect(() => {
    setToast([]);
    return () => { };
  }, []);

  const clientImageInputRef = useRef(null);
  const imageReader = new FileReader();

  const filterOptions = createFilterOptions();

  const StyledDiv = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    opacity: '0',
    position: 'absolute',
    height: '6.5em',
    width: '6.5em',
    borderRadius: clientImageUrl && clientImageUrl.length > 0 ? '0' : '50%',
    '&:hover': {
      opacity: '1',
      backgroundColor: 'rgba(128,128,128,0.1)',
    },

  })

  const addressTypeOptions = [
    {
      label: "Shipping",
      value: "shipping",
    },
    {
      label: "Billing",
      value: "billing",
    },
    {
      label: "Postal",
      value: "postal",
    },
    {
      label: "Building",
      value: "building",
    },
  ];

  const formik = useFormik({
    initialValues: {
      name: "",
      account: "",
      email: "",
      phone: "",
      website: "",
      notes: "",
      addresses: [
        {
          type: null,
          address_line_1: "",
          address_line_2: "",
          city: "",
          state_or_province: "",
          country_or_region: "",
          postal_code: "",
        },
      ],
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().max(255).required("Client Name is required"),
      account: Yup.string().trim(),
      email: Yup.string().email().trim(),
      phone: Yup.number(),
      website: Yup.string().url().trim(),
      notes: Yup.string().trim(),
      addresses: Yup.array().nullable(true),
    }),
    onSubmit: () => {
      async function submit() {
        let { name, account, email, phone, website, notes, addresses } =
          formik.values;
        let attributes = {
          name,
          account,
          email,
          phone,
          website,
          notes,
          addresses,
          image_type: clientImageFileType?.split("/")[1]
        };
        try {
          const payload = await onSubmit(attributes, currentOrgId);
          await submitClientImage(payload?.id);
          setCustomerSystemId(payload?.id);
          setOpen(true);
        } catch (error) {
          if (error.message === "Request failed with status code 405") {
            alert(
              "A client with the same name already exists. \nPlease use a different name."
            );
          } else {
            alert(error.message);
          }
          // alert(error);
          formik.setSubmitting(false);
        }
      }
      submit();
    },
  });

  const onSubmit = async (attributes, orgId) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const { addresses } = attributes;
      try {
        const payload = await createCustomer(attributes, orgId);
        if (addresses.length > 0) {
          let updatedAddresses = addresses.reduce((acc, address) => {
            if (address?.type?.label && address?.address_line_1 && address?.city && address?.state && address?.country && address?.postal_code){
              let body = {
                type: address?.type?.label,
                address_line_1: address?.address_line_1,
                address_line_2: address?.address_line_2,
                city: address?.city,
                state_or_province: address?.state ? address?.state : "",
                country_or_region: address?.country
                  ? address?.country?.label
                  : "",
                postal_code: address?.postal_code,
              };
              acc.push(body);
            }
            return acc;
          },[]);
          await postNewCustomerAddress(orgId, payload?.id, updatedAddresses);
        }
        resolve(payload);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleClose = () => {
    if (customerSystemId !== "null") {
      router.push({
        pathname: `/customers/${customerSystemId}/${currentOrgId}`,
      });
    }
    setOpen(false);
  };

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  const handleImageSelection = async (file) => {
    if (!file || !validateImageType(file)) {
      setToast([
        ...toast,
        {
          message: `Please upload valid images. Allowed types: jpeg, jpg, webp, png, and svg`,
          open: true,
          severity: "error",
        },
      ]);
      return false;
    }
    imageReader.onloadend = () => {
      setClientImageUrl(imageReader.result);
    }
    imageReader.readAsDataURL(file);
    setClientImageFileType(file?.type);
    setClientImage(file);
    setClientImageBtnProps(BUTTONS.EDIT_BUTTON);
    return true;
  }

  const removeClientImage = () => {
    setClientImageUrl("");
    setClientImageFileType("");
    setClientImageBtnProps(BUTTONS.ADD_BUTTON);
  };

  const submitClientImage = async (clientId, file) => {
    try {
      file = file || clientImage;
      if(file){
        const image_type = file.type.split("/")[1];
        const response = await getClientImagePutUrl(orgID?.id, clientId, image_type);

        if (response.status === 200 && response.data.putUrl) {
          const res = await putClientImage(response.data.putUrl, file, image_type);
          if (res.status === 200) {
            await updateOrgCustomerDetails(orgID?.id, clientId, { image_type: image_type });
            removeClientImage();
          }
        }
      }
      
    } catch (error) {
      setToast([
        ...toast,
        {
          message: error,
          open: true,
          severity: "error",
        },
      ]);
    }
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={() => {
          formik.handleReset();
          props.handleClose();
          setClientAvatarInitials("");
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        scroll="paper"
        fullWidth={true}
        maxWidth="xlg"
      >
        <>
          <DialogTitle sx={{ px: 2 }} id="alert-dialog-title" scroll="paper">
            <Stack sx={{ alignItems: "left" }}>
              <Typography fontSize={18} variant="h4">
                Create New Client
              </Typography>
            </Stack>
            {props.open ? (
              <IconButton
                aria-label="close"
                onClick={() => {
                  formik.handleReset();
                  props.handleClose();
                  setClientAvatarInitials("");
                }}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ px: 2 }}>
            <FormikProvider value={formik}>
              <Box component="main">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {clientImageUrl && clientImageUrl.length > 0 ? (
                        <img
                          alt={formik.values.clientName}
                          src={clientImageUrl}
                          style={{
                            height: "6.5rem",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <IconButton
                          onClick={() => clientImageInputRef.current.click()}
                        >
                          <Avatar
                            fontSize="large"
                            sx={{
                              height: "4.7em",
                              width: "4.7em",
                            }}
                            src={clientAvatarInitials && getAvatarInitials(clientAvatarInitials)}
                          />
                        </IconButton>
                      )}
                      <StyledDiv>
                        <Stack
                          direction="row"
                          justifyContent="space-around"
                          alignItems="center"
                        >
                          <IconButton
                            size={clientImageBtnProps.size}
                            onClick={() =>
                              clientImageInputRef.current.click()
                            }
                          >
                            <input
                              name="image_url"
                              ref={clientImageInputRef}
                              multiple={false}
                              style={{ display: "none" }}
                              type="file"
                              accept="image/*"
                              onChange={() =>
                                handleImageSelection(
                                  clientImageInputRef.current.files[0]
                                )
                              }
                            />

                            <ImageIcon />
                          </IconButton>
                          {clientImageUrl && clientImageUrl.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={removeClientImage}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </StyledDiv>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Client Name *
                      </Typography>
                    </Stack>
                    <TextField
                      autoComplete="off"
                      size="small"
                      error={Boolean(
                        formik.touched.name && formik.errors.name
                      )}
                      fullWidth
                      helperText={formik.touched.name && formik.errors.name}
                      placeholder="Type"
                      margin="normal"
                      sx={{ mt: "8px" }}
                      name="name"
                      onBlur={formik.handleBlur}
                      // onChange={formik.handleChange}
                      onChange={(event) => {
                        const value = event.target.value;
                        formik.setFieldValue("name", value);
                        setClientAvatarInitials(value);
                      }}
                      value={formik.values.name}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Account Number
                      </Typography>
                    </Stack>
                    <Stack direction="row">
                      <TextField
                        autoComplete="off"
                        size="small"
                        error={Boolean(
                          formik.touched.account && formik.errors.account
                        )}
                        fullWidth
                        helperText={
                          formik.touched.account && formik.errors.account
                        }
                        placeholder="Type"
                        margin="normal"
                        sx={{ mt: "8px" }}
                        name="account"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.account}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}></Grid>
                  <Grid item xs={12} md={3}></Grid>

                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Email
                      </Typography>
                    </Stack>
                    <TextField
                      autoComplete="off"
                      size="small"
                      error={Boolean(
                        formik.touched.email && formik.errors.email
                      )}
                      fullWidth
                      helperText={formik.touched.email && formik.errors.email}
                      placeholder="Type"
                      margin="normal"
                      sx={{ mt: "8px" }}
                      name="email"
                      type="email"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.email}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Phone
                      </Typography>
                    </Stack>
                    <Stack direction="row">
                      <TextField
                        autoComplete="off"
                        size="small"
                        error={Boolean(
                          formik.touched.phone && formik.errors.phone
                        )}
                        fullWidth
                        helperText={
                          formik.touched.phone && formik.errors.phone
                        }
                        placeholder="Type"
                        margin="normal"
                        sx={{ mt: "8px", paddingLeft: "0px !important" }}
                        name="phone"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.phone}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Website
                      </Typography>
                    </Stack>
                    <Stack direction="row">
                      <TextField
                        autoComplete="off"
                        size="small"
                        error={Boolean(
                          formik.touched.website && formik.errors.website
                        )}
                        fullWidth
                        helperText={
                          formik.touched.website && formik.errors.website
                        }
                        placeholder="Type"
                        margin="normal"
                        sx={{ mt: "8px" }}
                        name="website"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.website}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={3}></Grid>
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        Notes / Comments
                      </Typography>
                    </Stack>
                    <TextField
                      autoComplete="off"
                      size="small"
                      error={Boolean(
                        formik.touched.notes && formik.errors.notes
                      )}
                      fullWidth
                      helperText={formik.touched.notes && formik.errors.notes}
                      placeholder="Type"
                      margin="normal"
                      sx={{ mt: "8px" }}
                      name="notes"
                      onChange={formik.handleChange}
                      value={formik.values.notes}
                      onBlur={formik.handleBlur}
                      InputProps={{
                        inputComponent: TextareaAutosize,
                        inputProps: { minRows: 4 },
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Grid container sx={{ my: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Typography fontSize={18} variant="h4">
                      Address:
                    </Typography>
                  </Grid>
                </Grid>

                <FieldArray
                  name="addresses"
                  render={(arrayHelpers) => {
                    return (
                      <>
                        {formik.values.addresses.map((address, index) => (
                          <div key={index}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    Type
                                  </Typography>
                                </Stack>
                                <Autocomplete
                                  sx={{ mt: 1 }}
                                  disablePortal
                                  ListboxProps={{
                                    style: { maxHeight: "200px" },
                                  }}
                                  options={addressTypeOptions}
                                  autoHighlight
                                  size="small"
                                  getOptionLabel={(option) =>
                                    option.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    value === undefined ||
                                    value === "" ||
                                    option.label === value.label
                                  }
                                  margin="normal"
                                  name={`addresses[${index}].type`}
                                  limitTags={4}
                                  onBlur={formik.handleBlur}
                                  onChange={(value, newValue) => {
                                    formik.setFieldValue(
                                      `addresses[${index}].type`,
                                      newValue
                                    );
                                  }}
                                  fullWidth
                                  value={formik.values.addresses[index].type}
                                  renderOption={(props, option) => (
                                    <Box
                                      sx={{
                                        "& > img": { mr: 2, flexShrink: 0 },
                                      }}
                                      {...props}
                                    >
                                      <div style={{ marginLeft: 25 }}>
                                        {option.label}
                                      </div>
                                    </Box>
                                  )}
                                  filterOptions={(options, params) => {
                                    const defaultOptions = options.map(
                                      (option) => option.value
                                    );
                                    const filteredOptions = filterOptions(
                                      options,
                                      params
                                    );
                                    if (
                                      params.inputValue !== "" &&
                                      !defaultOptions.includes(
                                        params.inputValue
                                      )
                                    ) {
                                      filteredOptions.push({
                                        label: params.inputValue,
                                        value: params.inputValue,
                                      });
                                    }
                                    return filteredOptions;
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      placeholder="Select address type"
                                      inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "off", // disable autocomplete and autofill
                                      }}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    Address 1
                                  </Typography>
                                </Stack>
                                <TextField
                                  autoComplete="off"
                                  size="small"
                                  fullWidth
                                  placeholder="Type"
                                  margin="normal"
                                  sx={{ mt: 1 }}
                                  name={`addresses[${index}].address_line_1`}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  value={
                                    formik.values.addresses[index]
                                      .address_line_1
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    City
                                  </Typography>
                                </Stack>
                                <TextField
                                  autoComplete="off"
                                  size="small"
                                  fullWidth
                                  placeholder="Type"
                                  margin="normal"
                                  sx={{ mt: 1 }}
                                  name={`addresses[${index}].city`}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  value={formik.values.addresses[index].city}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    Country / Region
                                  </Typography>
                                </Stack>
                                <Autocomplete
                                  sx={{ mt: 1 }}
                                  disablePortal
                                  ListboxProps={{
                                    style: { maxHeight: "200px" },
                                  }}
                                  options={countries}
                                  autoHighlight
                                  size="small"
                                  getOptionLabel={(option) =>
                                    option.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    value === undefined ||
                                    value === "" ||
                                    option.label === value.label
                                  }
                                  margin="normal"
                                  name={`addresses[${index}].country`}
                                  limitTags={4}
                                  onBlur={formik.handleBlur}
                                  onChange={(value, newValue) => {
                                    formik.setFieldValue(
                                      `addresses[${index}].country`,
                                      newValue
                                    );
                                  }}
                                  fullWidth
                                  value={
                                    formik.values.addresses[index].country
                                  }
                                  renderOption={(props, option) => (
                                    <Box
                                      component="li"
                                      sx={{
                                        "& > img": { mr: 2, flexShrink: 0 },
                                      }}
                                      {...props}
                                    >
                                      <img
                                        loading="lazy"
                                        width="20"
                                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                        alt=""
                                      />
                                      {option.label} ({option.code}) +
                                      {option.phone}
                                    </Box>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      placeholder="Select"
                                      inputProps={{
                                        ...params.inputProps,
                                        autoComplete: "off", // disable autocomplete and autofill
                                      }}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}></Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    Address 2
                                  </Typography>
                                </Stack>
                                <TextField
                                  autoComplete="off"
                                  size="small"
                                  fullWidth
                                  placeholder="Type"
                                  margin="normal"
                                  sx={{ mt: 1 }}
                                  name={`addresses[${index}].address_line_2`}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  value={
                                    formik.values.addresses[index]
                                      .address_line_2
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    State / Province
                                  </Typography>
                                </Stack>
                                <TextField
                                  autoComplete="off"
                                  size="small"
                                  fullWidth
                                  placeholder="Type"
                                  margin="normal"
                                  sx={{ mt: 1 }}
                                  name={`addresses[${index}].state`}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  value={formik.values.addresses[index].state}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography
                                    fontSize={12}
                                    variant="h4"
                                    fontWeight="700"
                                  >
                                    Zip / Postal Code
                                  </Typography>
                                </Stack>
                                <TextField
                                  autoComplete="off"
                                  size="small"
                                  fullWidth
                                  placeholder="Type"
                                  margin="normal"
                                  sx={{ mt: 1 }}
                                  name={`addresses[${index}].postal_code`}
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  value={
                                    formik.values.addresses[index].postal_code
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                          </div>
                        ))}
                        <Grid container sx={{ my: 1 }}>
                          <Grid item xs={12} md={3}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography
                                fontSize={12}
                                variant="h4"
                                fontWeight="700"
                              >
                                <Button
                                  type="button"
                                  size="small"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      type: "",
                                      address_line_1: "",
                                      address_line_2: "",
                                      city: "",
                                      state: "",
                                      country: "",
                                      postal_code: "",
                                    })
                                  }
                                  startIcon={<AddCircleOutlineIcon />}
                                >
                                  Add another address
                                </Button>
                              </Typography>
                            </Stack>
                          </Grid>
                        </Grid>
                      </>
                    );
                  }}
                />

                <Divider sx={{ my: 2 }} />
                <Grid container sx={{ my: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={12} variant="h4" fontWeight="700">
                        {/* <Button
                        type="button"
                        size="small"
                        onClick={onAddBtnClick}
                        startIcon={<AddCircleOutlineIcon />}
                      >
                        Add another address
                      </Button> */}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </FormikProvider>

            <AlertDialog
              handleClose={handleClose}
              open={open}
              name={formik.values.name}
            />
          </DialogContent>
          <DialogActions
            sx={
              {
                // justifyContent: "space-between",
              }
            }
          >
            {/* <Button onClick={props.handleClose} variant="contained">
              Ok
            </Button> */}

            <div>
              <Button
                sx={{ mr: 2 }}
                color="primary"
                // disabled={formik.isSubmitting}
                fullWidth
                size="large"
                // onClick={() => router.goBack()}
                onClick={() => {
                  props.handleClose();
                  formik.handleReset();
                  setClientAvatarInitials("");
                }}
                // type="submit"
                variant="text"
              >
                Cancel
              </Button>
            </div>
            <div>
              <Button
                sx={{ mr: 2 }}
                color="primary"
                disabled={
                  !(formik.isValid && formik.dirty && !formik.isSubmitting)
                }
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                onClick={formik.handleSubmit}
              >
                Save
              </Button>
            </div>
          </DialogActions>
        </>
        {toast.map((item, id) => {
          return (
            <Snackbar
              key={id}
              open={item.open}
              autoHideDuration={6000}
              onClose={() => {
                setToast(toast.filter((v, i) => i !== id));
              }}
            >
              <Alert
                onClose={() => {
                  setToast(toast.filter((v, i) => i !== id));
                }}
                severity={item.severity}
                sx={{ width: "100%" }}
                variant="filled"
              >
                {item.message}
              </Alert>
            </Snackbar>
          );
        })}
      </Dialog>
    </div>
  );
}
